import * as moment from 'moment';

import { type ShipmentStatus } from '~/types/Shipment';
import { type TransactionStatus } from '~/types/Transaction';

import { type BaseAddress } from './Address';

enum BatchGroupStatus {
  COMPLETE = 'COMPLETE',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
}

enum BatchEntryStatus {
  COMPLETE_SHIPMENT = 'COMPLETE_SHIPMENT',
  COMPLETE_TRANSACTION = 'COMPLETE_TRANSACTION',
  EXCLUDED_ORDER = 'EXCLUDED_ORDER',
  INVALID_ORDER = 'INVALID_ORDER',
  PENDING = 'PENDING',
  PROCESSING_SHIPMENT = 'PROCESSING_SHIPMENT',
  PROCESSING_TRANSACTION = 'PROCESSING_TRANSACTION',
  RATE_NOT_AVAILABLE = 'RATE_NOT_AVAILABLE',
  SHIPMENT_REQUEST_FAILED = 'SHIPMENT_REQUEST_FAILED',
  SHIPMENT_REQUEST_NOT_AVAILABLE = 'SHIPMENT_REQUEST_NOT_AVAILABLE',
  TRANSACTION_REQUEST_FAILED = 'TRANSACTION_REQUEST_FAILED',
  VALID_ORDER = 'VALID_ORDER',
  VALIDATING_ORDER = 'VALIDATING_ORDER',
}

type JSONBatchTransaction = {
  batch_entry_id: number;
  object_created: string;
  object_id: string;
  object_status: string;
  object_updated: string;
};

type JSONBatchShipment = {
  batch_entry_id: number;
  num_rates: number;
  object_created: string;
  object_id: string;
  object_status: string;
  object_updated: string;
  selected_rate_object_id: string;
};

type JSONBatchEntry = {
  id: number;
  is_return: boolean;
  object_created: string;
  object_status: string;
  object_updated: string;
  order_object_id: string;
};

interface WEBJSONBatchEntry extends Omit<JSONBatchEntry, 'object_status'> {
  object_status?: string;
  order_id?: string;
  status?: string;
}

type JSONBatchOrder = {
  errors: string[];
  object_created: string;
  object_id: string;
  object_updated: string;
  validation_status: Maybe<string>;
};

type JSONOrdersBatch = {
  batch_entries: JSONBatchEntry[];
  id: number;
  label_limit_renewal_date?: string;
  object_created: string;
  object_updated: string;
  order_group_status: string;
  orders: JSONBatchOrder[];
  remaining_labels_until_limit?: number;
  return_shipment_group_status: string;
  return_transaction_group_status: string;
  shipment_group_status: string;
  shipments: JSONBatchShipment[];
  transaction_group_status: string;
  transactions: JSONBatchTransaction[];
};

type Parcel = {
  distanceUnit: string;
  height: string;
  length: string;
  templateToken?: string;
  useImportedOrderWeight: boolean;
  userParcelTemplateId?: Maybe<string>;
  weight: string;
  weightUnit: string;
  width: string;
};

type JSONParcel = {
  distance_unit: string;
  height: string;
  length: string;
  mass_unit: string;
  template: string;
  useImportedOrderWeight: boolean;
  weight: string;
  width: string;
};

const parcelFromJSON = (jsonParcel: JSONParcel): Parcel => ({
  distanceUnit: jsonParcel.distance_unit,
  height: jsonParcel.height,
  length: jsonParcel.length,
  templateToken: jsonParcel.template,
  useImportedOrderWeight: false, // TODO: this should not even be here
  weight: jsonParcel.weight,
  weightUnit: jsonParcel.mass_unit,
  width: jsonParcel.width,
});

type UserBatchInput = {
  addressFrom?: BaseAddress;
  addressReturn?: BaseAddress;
  extra: {
    insurance: {
      amount: string;
      content: string;
      currency: string;
      useImportedOrderValue: boolean;
    };
    isResidential?: boolean;
    isReturn: boolean;
    signatureConfirmation: boolean;
  };
  parcel: Parcel;
  shipmentDate: moment.Moment;
};

type BatchTransaction = {
  batchEntryId: number;
  objectCreated: moment.Moment;
  objectId: string;
  objectStatus: TransactionStatus;
  objectUpdated: moment.Moment;
};

type BatchShipment = {
  batchEntryId: number;
  numRates: number;
  objectCreated: moment.Moment;
  objectId: string;
  objectStatus: ShipmentStatus;
  objectUpdated: moment.Moment;
  selectedRateObjectId: string;
};

type BatchEntryId = number;

type BatchEntry = {
  id: BatchEntryId;
  isReturn: boolean;
  objectCreated: moment.Moment;
  objectStatus: BatchEntryStatus;
  objectUpdated: moment.Moment;
  orderObjectId: string;
  /**
   * This is only used for return labels
   * It's the ID of the original batch entry ID
   */
  returnOfID?: null | number;

  userInput?: UserBatchInput;
};

type BatchOrder = {
  errors: string[];
  objectCreated: moment.Moment;
  objectId: string;
  objectUpdated: moment.Moment;
  validationStatus: Maybe<string>;
};

type OrdersBatch = {
  batchEntries: BatchEntry[];
  id: number;
  labelLimitRenewalDate?: string;
  objectCreated: moment.Moment;
  objectUpdated: moment.Moment;
  orderGroupStatus: BatchGroupStatus;
  orders: BatchOrder[];
  remainingLabels?: number;
  returnShipmentGroupStatus: BatchGroupStatus;
  returnTransactionGroupStatus: BatchGroupStatus;
  shipmentGroupStatus: BatchGroupStatus;
  shipments: BatchShipment[];
  transactionGroupStatus: BatchGroupStatus;
  transactions: BatchTransaction[];
};

enum BatchSteps {
  LABELS = 'LABELS',
  NONE = 'NONE',
  ORDERS_PAGE = 'ORDERS_PAGE',
  PACKAGE_INFORMATION = 'PACKAGE_INFORMATION',
  RATES = 'RATES',
}

const batchTransactionFromJSON = (
  jsonBatchTransaction: JSONBatchTransaction,
): BatchTransaction => ({
  batchEntryId: jsonBatchTransaction.batch_entry_id,
  objectCreated: moment(jsonBatchTransaction.object_created),
  objectId: jsonBatchTransaction.object_id,
  objectStatus: <TransactionStatus>jsonBatchTransaction.object_status,
  objectUpdated: moment(jsonBatchTransaction.object_updated),
});

const batchShipmentFromJSON = (jsonBatchShipment: JSONBatchShipment): BatchShipment => ({
  batchEntryId: jsonBatchShipment.batch_entry_id,
  numRates: jsonBatchShipment.num_rates,
  objectCreated: moment(jsonBatchShipment.object_created),
  objectId: jsonBatchShipment.object_id,
  objectStatus: <ShipmentStatus>jsonBatchShipment.object_status,
  objectUpdated: moment(jsonBatchShipment.object_updated),
  selectedRateObjectId: jsonBatchShipment.selected_rate_object_id,
});

const batchEntriesFromJSON = (jsonBatchEntry: WEBJSONBatchEntry): BatchEntry => ({
  id: jsonBatchEntry.id,
  isReturn: jsonBatchEntry.is_return,
  objectCreated: moment(jsonBatchEntry.object_created),
  objectStatus: <BatchEntryStatus>jsonBatchEntry.object_status || jsonBatchEntry.status,
  objectUpdated: moment(jsonBatchEntry.object_updated),
  orderObjectId: jsonBatchEntry.order_object_id || jsonBatchEntry.order_id || '',
});

const batchOrderFromJSON = (jsonBatchOrder: JSONBatchOrder): BatchOrder => ({
  errors: jsonBatchOrder.errors,
  objectCreated: moment(jsonBatchOrder.object_created),
  objectId: normalizeObjectId(jsonBatchOrder.object_id),
  objectUpdated: moment(jsonBatchOrder.object_updated),
  validationStatus: jsonBatchOrder.validation_status,
});

const fromJSON = (jsonOrdersBatch: JSONOrdersBatch): OrdersBatch => ({
  batchEntries: jsonOrdersBatch.batch_entries.map(batchEntriesFromJSON),
  id: jsonOrdersBatch.id,
  labelLimitRenewalDate: jsonOrdersBatch.label_limit_renewal_date,
  objectCreated: moment(jsonOrdersBatch.object_created),
  objectUpdated: moment(jsonOrdersBatch.object_updated),
  orderGroupStatus: <BatchGroupStatus>jsonOrdersBatch.order_group_status,
  orders: jsonOrdersBatch.orders.map(batchOrderFromJSON),
  remainingLabels: jsonOrdersBatch.remaining_labels_until_limit,
  returnShipmentGroupStatus: <BatchGroupStatus>jsonOrdersBatch.return_shipment_group_status,
  returnTransactionGroupStatus: <BatchGroupStatus>jsonOrdersBatch.return_transaction_group_status,
  shipmentGroupStatus: <BatchGroupStatus>jsonOrdersBatch.shipment_group_status,
  shipments: jsonOrdersBatch.shipments.map(batchShipmentFromJSON),
  transactionGroupStatus: <BatchGroupStatus>jsonOrdersBatch.transaction_group_status,
  transactions: jsonOrdersBatch.transactions.map(batchTransactionFromJSON),
});

const isFailedBatchEntry = (batchEntry: BatchEntry) =>
  batchEntry.objectStatus === BatchEntryStatus.INVALID_ORDER ||
  batchEntry.objectStatus === BatchEntryStatus.SHIPMENT_REQUEST_FAILED ||
  batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED;

// Move to utils
const normalizeObjectId = (objectId: string): string => objectId.replace(new RegExp('-', 'g'), '');

export default OrdersBatch;
export {
  BatchEntry,
  BatchEntryId,
  BatchEntryStatus,
  BatchGroupStatus,
  BatchOrder,
  BatchShipment,
  BatchSteps,
  BatchTransaction,
  JSONBatchEntry,
  JSONOrdersBatch,
  JSONParcel,
  OrdersBatch,
  Parcel,
  UserBatchInput,
  batchEntriesFromJSON,
  fromJSON,
  isFailedBatchEntry,
  parcelFromJSON,
};
